<template>
  <div class="pa1">
    <div class="text-center">
      <DocumentThumbnail
        v-if="document && document.url"
        :mimeType="document.mime_type"
        :thumbnail="document.url"
        :version="previewVersion"
        :title="document.title"
        :alt="document.title"
        :width="0"
        :height="0"
        style="background: #c9c9c9; padding: 5px"
      />
      <img
        v-else
        src="/imperia/images/undraw/edit_item.svg"
        class="empty-placeholder"
      />
    </div>

    <table
      v-if="document"
      class="mam_info_panel border-separate"
      style="border-spacing: 5px 10px"
      cellapdding="0"
      cellspacing="0"
    >
      <tbody class="info_panel_content">

        <tr v-if="!isHiddenField('title')">
          <th>{{ LANG.title }}</th>
          <td>{{ document.title || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('directory')">
          <th>{{ LANG.directory }}</th>
          <td>{{ document.directory || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('url') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.uri }}</th>
          <td>
            <a
              :href="document.url"
              :aria-label="`${LANG.preview} ${LANG.opensBlank}`"
              target="_blank"
            >
              {{ document.url || "-" }}
            </a>
          </td>
        </tr>

        <tr v-if="!isHiddenField('filename')">
          <th>{{ LANG.filename }}</th>
          <td>{{ document.filename || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('last_editor')">
          <th>{{ LANG.lastEditor }}</th>
          <td>{{ document.last_editor || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('finished_at') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.lastFinished }}</th>
          <td>{{ formatDateLocale(document.finished_at) || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('published_at') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.publishedAt }}</th>
          <td>{{ formatDateLocale(document.published_at) || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('category')">
          <th>{{ LANG.category }}</th>
          <td>{{ document.category || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('publish_date') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.publishDate }}</th>
          <td>{{ formatDateLocale(document.publish_date) || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('expiry_date') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.expiryDate }}</th>
          <td>{{ formatDateLocale(document.expiry_date) || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('resubmit_date') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.resubmitDate }}</th>
          <td>{{ formatDateLocale(document.resubmit_date) || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('created_by')">
          <th>{{ LANG.createdBy }}</th>
          <td>{{ document.created_by || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('created_at')">
          <th>{{ LANG.firstCreatedDate }}</th>
          <td>{{ formatDateLocale(document.created_at) || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('live_url') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.liveURI }}</th>
          <td><DocumentFieldLiveUri :item="document" v-bind="$attrs" /></td>
        </tr>

        <tr v-if="!isHiddenField('published') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.published }}</th>
          <td><DocumentFieldUpToDate :item="document" v-bind="$attrs" /></td>
        </tr>

        <tr v-if="!isHiddenField('references')">
          <th>{{ LANG.references }}</th>
          <td><DocumentFieldReferences :item="document" v-bind="$attrs" /></td>
        </tr>

        <tr v-if="!isHiddenField('dimensions') && browserMode === BrowserMode.MAM">
          <th>{{ LANG.dimensions }}</th>
          <td>{{ document.dimensions || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('filesize') && browserMode === BrowserMode.MAM">
          <th>{{ LANG.filesize }}</th>
          <td>{{ humanFileSize(document.filesize) || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('template') && browserMode === BrowserMode.MAM">
          <th>{{ LANG.template }}</th>
          <td>{{ document.template || "-" }}</td>
        </tr>

        <tr v-for="{name, label} in customColumns" :key="name">
          <th>{{ label }}</th>
          <td>{{ document[name] || "-" }}</td>
        </tr>

        <tr v-if="!isHiddenField('edit')">
          <th>{{ LANG.edit }}</th>
          <td>
            <i-action-link
              icon="info-circle"
              :href="uri('/document/archive/import', { __imperia_method: 'edit', __imperia_node_id: document.node_id })"
              :enabled="checkACL('/document/archive/import') && document.can_edit"
            >
              {{ LANG.edit }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('quickedit') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.quickedit }}</th>
          <td>
            <i-action-link
              icon="pen-square"
              :href="uri('/document/archive/import', { __imperia_method: 'quickEdit', __imperia_node_id: document.node_id })"
              :enabled="true"
            >
              {{ LANG.quickedit }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('variants') && browserMode === BrowserMode.MAM">
          <th>{{ LANG.viewVariants }}</th>
          <td>
            <i-action-link
              icon="eye"
              @click.prevent="$emit('documentAction', { action: 'variants', document })"
              :enabled="checkACL('/document/media/show_variants')"
            >
              {{ LANG.viewVariants }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('focuspoint') && browserMode === BrowserMode.MAM">
          <th>{{ LANG.focusPoint }}</th>
          <td>
            <i-action-link
              icon="tasks"
              :href="uri('/document/media/focus_area/show', { node_id: document.node_id })"
              @click.prevent="$emit('documentAction', { action: 'focusPoint', document })"
              :enabled="checkACL('/document/media/focus_area/show')"
            >
              {{ LANG.focusPoint }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('customize') && browserMode === BrowserMode.MAM">
          <th>{{ LANG.customize }}</th>
          <td>
            <i-action-link
              icon="edit"
              @click.prevent="() => showActions(document.node_id)"
              :enabled="checkACL('/document/media/customize') && document.can_edit"
            >
              {{ LANG.customize }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('publish')">
          <th>{{ LANG.publish }}</th>
          <td>
            <i-action-link
              icon="globe"
              :href="uriPublish"
              :enabled="checkACL('/document/publisher') && document.can_edit"
            >
              {{ LANG.publish }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('info')">
          <th>{{ LANG.information }}</th>
          <td>
            <i-action-link
              icon="info-circle"
              :href="uri('/document/oce/info', { __imperia_node_id: document.node_id })"
              @click.prevent="$emit('documentAction', { action: 'info', document })"
              :enabled="checkACL('/document/oce/info')"
            >
              {{ LANG.information }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('differences')">
          <th>{{ LANG.differences }}</th>
          <td>
            <i-action-link
              icon="ballot-check"
              :href="uri('/document/diff_select', { __imperia_node_id: document.node_id })"
              @click.prevent="$emit('documentAction', { action: 'differences', document })"
              :enabled="checkACL('/document/diff_select')"
            >
              {{ LANG.differences }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('history') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.history }}</th>
          <td>
            <i-action-link
              icon="history"
              :href="uri('/document/view_history', { __imperia_node_id: document.node_id, standalone: '1' })"
              @click.prevent="$emit('documentAction', { action: 'history', document })"
              :enabled="checkACL('/document/view_history')"
            >
              {{ LANG.history }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('preview') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.preview }}</th>
          <td>
            <i-action-link
              icon="sun"
              :href="uri('/document/preview_url', { __imperia_url: document.url })"
              :enabled="checkACL('/document/preview_url')"
            >
              {{ LANG.preview }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('reparse') && browserMode === BrowserMode.DOC">
          <th>{{ LANG.reparse }}</th>
          <td>
            <i-action-link
              icon="sync"
              :href="uri('/structure/template_reparser/reparse', { node_id: document.node_id, type: 'generate', mode: 'pending' })"
              :enabled="checkACL('/structure/template_reparser/reparse') && document.can_edit"
            >
              {{ LANG.reparse }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('unpublish')">
          <th>{{ LANG.unpublish }}</th>
          <td>
            <i-action-link
              icon="globe"
              :href="uriUnpublish"
              :enabled="checkACL('/document/publisher') && document.can_delete && Boolean(document.published_at)"
            >
              {{ LANG.unpublish }}
            </i-action-link>
          </td>
        </tr>

        <tr v-if="!isHiddenField('delete')">
          <th>{{ LANG.delete }}</th>
          <td>
            <i-action-link
              icon="times"
              :href="uriDelete"
              :enabled="checkACL('/document/media/content/published/mass_action/delete') && document.can_delete"
            >
              {{ LANG.delete }}
            </i-action-link>
          </td>
        </tr>

      </tbody>
    </table>

    <div class="pa2 text-center" v-else>
      {{ LANG.clickOnAnyItemToViewTheDetails }}
    </div>

  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import { useStore } from 'vuex';

import globals from '@/globals';
import { humanFileSize, formatDateLocale } from '@/utils/tools';
import { ControllerParamFormat } from '@/components/browser/panel_center/PanelCenterFooterMassActions.vue';
import { FinishController, BrowserMode } from '@/store/modules/entity_browser/config';

import DocumentThumbnail from '@/components/browser/documents/DocumentThumbnail.vue';
import DocumentFieldUpToDate from '@/components/browser/documents/DocumentFieldUpToDate.vue';
import DocumentFieldLiveUri from '@/components/browser/documents/DocumentFieldLiveUri.vue';
import DocumentFieldReferences from '@/components/browser/documents/DocumentFieldReferences.vue';
import DocumentInspectorActionLink from './DocumentInspectorActionLink.vue';

function docRef(doc) {
  return {
    nodeId: doc.node_id,
    url: doc.master ? null : doc.url,
  };
}

const { uri } = globals.I.Util;

export default defineComponent({
  components: {
    [DocumentInspectorActionLink.name]: DocumentInspectorActionLink,
    DocumentThumbnail,
    DocumentFieldReferences,
    DocumentFieldLiveUri,
    DocumentFieldUpToDate,
  },
  props: {
    document: {
      type: Object,
    },
  },
  setup(props) {
    const store = useStore();
    const browserMode = computed(() => store.state.browserMode);
    return {
      browserMode,
      BrowserMode,
      LANG: computed(() => store.state.LANG),
      checkACL: computed(() => store.getters.checkACL),
      isHiddenField: computed(() => store.getters[`${store.state.browserMode}/isHiddenField`]),
      customColumns: computed(() => store.getters[`${store.state.browserMode}/customColumns`]),
      humanFileSize,
      formatDateLocale,
      uri,
      showActions: globals?.I?.MAM?.showActions,
      previewVersion: computed(() => btoa(props.document.finished_at)),
    };
  },
  computed: {
    uriPublish() {
      return uri('/document/publisher', {
        ...ControllerParamFormat.publish(docRef(this.document)),
        ignore_cache: '1',
        finish_controller: FinishController[this.browserMode],
      });
    },
    uriUnpublish() {
      return uri('/document/publisher', {
        ...ControllerParamFormat.unpublish(docRef(this.document)),
        ignore_cache: '1',
        finish_controller: FinishController[this.browserMode],
      });
    },
    uriDelete() {
      return uri('/document/media/content/published/mass_action/delete', {
        ...ControllerParamFormat.delete(docRef(this.document)),
        finish_controller: FinishController[this.browserMode],
      });
    },
  },
});
</script>

<style scoped lang="scss">
.empty-placeholder {
  margin: 30px 0px 30px 0px;
  max-width: 250px;
}
</style>
